import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAP, BOOKING_URL } from "../lib/config";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/rooms", label: "Rooms" },
  { to: "/gallery", label: "Gallery" },
  { to: "/amenities", label: "Amenities" },
  { to: "/local-guide", label: "Local Guide" },
  { to: "/blog", label: "Journal" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const MOBILE_TRIPS = [
  { to: "/truman-lake-fishing", label: "Truman Lake Fishing" },
  { to: "/boaters", label: "Boaters & Anglers" },
  { to: "/katy-trail-cyclists", label: "Katy Trail Cyclists" },
  { to: "/pet-friendly", label: "Pet Friendly" },
  { to: "/reunions", label: "Reunions & Festivals" },
  { to: "/extended-stay", label: "Work Crews & Truckers" },
  { to: "/lake-activities", label: "KC Weekenders" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 border-b transition-colors duration-300 ${
        scrolled
          ? "bg-[#FDFBF7]/95 backdrop-blur-md border-[#E5DFD3]"
          : "bg-[#FDFBF7]/90 backdrop-blur-sm border-transparent"
      }`}
    >
      <div className="wb-container">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className="flex items-center gap-3"
            data-testid="header-logo-link"
            onClick={() => setOpen(false)}
          >
            <img
              src="/images/logo-horizontal.png"
              alt="Westbridge Inn & Suites"
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-bold rounded-sm ${
                    isActive
                      ? "text-[#B94A15]"
                      : "text-[#1A1A1A] hover:text-[#B94A15]"
                  }`
                }
                style={{ fontFamily: "'Nunito', sans-serif" }}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={`tel:${NAP.phoneTel}`}
              data-testid="header-phone-link"
              className="flex items-center gap-2 text-[#1A1A1A] hover:text-[#B94A15] font-bold"
              style={{ fontFamily: "'Nunito', sans-serif" }}
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm">{NAP.phone}</span>
            </a>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-book-now"
              className="wb-btn-primary text-sm py-2.5"
            >
              Book Now
            </a>
          </div>

          <button
            className="lg:hidden p-2 text-[#1A1A1A]"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="header-mobile-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#FDFBF7] border-t border-[#E5DFD3] max-h-[80vh] overflow-y-auto">
          <nav className="wb-container py-4 flex flex-col gap-1" aria-label="Mobile">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-3 text-base font-bold rounded-sm ${
                    isActive
                      ? "text-[#B94A15] bg-[#FCEEEA]"
                      : "text-[#1A1A1A] hover:bg-[#F5F0E6]"
                  }`
                }
                style={{ fontFamily: "'Nunito', sans-serif" }}
              >
                {n.label}
              </NavLink>
            ))}
            <NavLink
              to="/accessibility"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-accessibility"
              className={({ isActive }) =>
                `px-3 py-3 text-base font-bold rounded-sm ${
                  isActive ? "text-[#B94A15] bg-[#FCEEEA]" : "text-[#1A1A1A] hover:bg-[#F5F0E6]"
                }`
              }
              style={{ fontFamily: "'Nunito', sans-serif" }}
            >
              Accessibility
            </NavLink>

            <div className="mt-4 pt-4 border-t border-[#E5DFD3]">
              <div className="wb-eyebrow px-3 mb-2">Trips</div>
              {MOBILE_TRIPS.map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-trip-${t.to.slice(1)}`}
                  className="block px-3 py-2 text-sm text-[#1A1A1A] hover:text-[#B94A15]"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {t.label}
                </NavLink>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-[#E5DFD3]">
              <a
                href={`tel:${NAP.phoneTel}`}
                className="px-3 py-3 text-base font-bold text-[#1A1A1A] block"
                data-testid="mobile-nav-phone"
                style={{ fontFamily: "'Nunito', sans-serif" }}
              >
                Call {NAP.phone}
              </a>
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="wb-btn-primary mt-2 w-full"
                data-testid="mobile-nav-book"
              >
                Book Now
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
